import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import {
  Bar,
  Cell,
  CartesianGrid,
  ComposedChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  collection,
  doc,
  getDoc,
  onSnapshot,
  orderBy,
  query,
  where,
} from "firebase/firestore";

import { db } from "../Config/firebase";
import Navbar from "../Components/Navbar/navbar";
import {
  DIMENSOES_PSICOSSOCIAIS,
  calcularMediaRiscosPsicossociais,
  calcularDimensoesPsicossociais,
  normalizarRespostasPsicossociais,
  classificarRiscoMatriz,
} from "./psicossocialUtils";
const DIMENSOES = [
  {
    id: "demandas_trabalho",
    nome: "DEMANDAS DE TRABALHO",
    perguntas: ["1A", "1B", "2A", "2B", "3A", "3B"],
    max: 24,
  },

  {
    id: "organizacao_conteudo",
    nome: "ORGANIZAÇÃO E CONTEÚDO DO TRABALHO",
    perguntas: ["4A", "4B", "5A", "5B", "6A", "6B", "7A", "7B"],
    max: 32,
  },

  {
    id: "relacoes_interpessoais",
    nome: "RELAÇÕES INTERPESSOAIS",
    perguntas: [
      "8A",
      "8B",
      "9A",
      "9B",
      "10A",
      "10B",
      "11A",
      "11B",
      "12A",
      "12B",
    ],
    max: 40,
  },

  {
    id: "interface_trabalho_individuo",
    nome: "INTERFACE TRABALHO-INDIVÍDUO",
    perguntas: ["13", "14A", "14B"],
    max: 9,
  },

  {
    id: "valores_local_trabalho",
    nome: "VALORES DO LOCAL DE TRABALHO",
    perguntas: ["15A", "15B", "16A", "16B"],
    max: 16,
  },

  {
    id: "saude_bem_estar",
    nome: "SAÚDE E BEM ESTAR",
    perguntas: ["17", "18A", "18B", "19A", "19B"],
    max: 20,
  },

  {
    id: "comportamentos_ofensivos",
    nome: "COMPORTAMENTOS OFENSIVOS",
    perguntas: ["20", "21", "22", "23", "24", "25", "26"],
    max: 7,
  },
];

function toDate(data) {
  if (!data) return null;
  if (data.toDate) return data.toDate();
  return new Date(data);
}

function formatarData(data) {
  const d = toDate(data);
  return d ? d.toLocaleString("pt-BR") : "-";
}

function getValorResposta(valor) {
  if (valor == null || valor === "") return 0;

  if (typeof valor === "number") return valor;

  if (typeof valor === "object" && valor.valor != null) {
    return Number(valor.valor || 0);
  }

  const numero = Number(valor);
  return Number.isFinite(numero) ? numero : 0;
}

function calcularDimensoes(respostas = {}) {
  return DIMENSOES.map((dim) => {
    const total = dim.perguntas.reduce((soma, codigo) => {
      return soma + getValorResposta(respostas[codigo]);
    }, 0);

    const percentual =
  dim.max > 0
    ? Number(((total / dim.max) * 5).toFixed(1))
    : 0;

    return {
      ...dim,
      total,
      percentual,
    };
  });
}

function classificarRisco(mediaGeral, dimensoes = []) {
  const getValor = (d) => Number(d.percentual ?? d.media ?? 0);

  const muitoAltas = dimensoes.filter((d) => getValor(d) >= 5).length;
  const altas = dimensoes.filter((d) => getValor(d) >= 4).length;
  const medias = dimensoes.filter((d) => getValor(d) >= 3).length;

  if (muitoAltas >= 1) {
    return {
      label: "Risco Muito Alto",
      color: "error",
    };
  }

  if (altas >= 2 || mediaGeral >= 4) {
    return {
      label: "Risco Alto",
      color: "warning",
    };
  }

  if (medias >= 1 || mediaGeral >= 3) {
    return {
      label: "Risco Médio",
      color: "warning",
    };
  }

  return {
    label: "Risco Baixo",
    color: "success",
  };
}

function obterCorRisco(valor) {
  if (valor >= 5) return "#ff0000";
  if (valor >= 4) return "#f4b400";
  if (valor >= 3) return "#ffff00";
  if (valor >= 2) return "#00aa00";
  return "#a8d08d";
}

export default function AdminPsicossocialRespostas() {
  const [respostas, setRespostas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroKpi, setFiltroKpi] = useState("");
 

  useEffect(() => {
    const q = query(
      collection(db, "kpi_respostas"),
      where("tipoFormulario", "==", "PSICOSSOCIAL"),
      orderBy("createdAt", "desc")
    );

    const unsubscribe = onSnapshot(q, async (snap) => {
      const userCache = new Map();

      const lista = await Promise.all(
        snap.docs.map(async (docResp) => {
          const data = docResp.data();

          let usuario = {};
          if (data.userId) {
            if (!userCache.has(data.userId)) {
              userCache.set(data.userId, getDoc(doc(db, "users", data.userId)));
            }

            const userSnap = await userCache.get(data.userId);
            if (userSnap.exists()) usuario = userSnap.data();
          }

          const respostasNormalizadas = normalizarRespostasPsicossociais(
            data.respostas || {},
            data.versaoPontuacaoPsicossocial
          );
          const dimensoes = calcularDimensoesPsicossociais(respostasNormalizadas);
          const mediaGeral =
            calcularMediaRiscosPsicossociais(respostasNormalizadas);

          return {
            id: docResp.id,
            ...data,
            respostas: respostasNormalizadas,
            usuarioNome: usuario.nome || data.usuarioNome || "Anônimo",
            departamento: data.departamento || data.setor || "Não informado",
            dimensoes,
            mediaGeral: Number(mediaGeral.toFixed(1)),
          };
        })
      );

      setRespostas(lista);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

 const respostasFiltradas = useMemo(() => {
  return respostas.filter((r) => {
    if (filtroKpi && r.kpiId !== filtroKpi) return false;
    return true;
  });
}, [respostas, filtroKpi]);

  const kpis = useMemo(() => {
    const map = new Map();
    respostas.forEach((r) => {
      if (r.kpiId) map.set(r.kpiId, r.kpiNome || r.nomeKpi || "Pesquisa psicossocial");
    });
    return Array.from(map.entries());
  }, [respostas]);

  const resumoDimensoes = useMemo(() => {
    return DIMENSOES.map((dim) => {
      const valores = respostasFiltradas
        .map((r) => r.dimensoes.find((d) => d.id === dim.id)?.percentual)
        .filter((v) => v != null);

      const media =
        valores.length > 0
          ? valores.reduce((soma, v) => soma + v, 0) / valores.length
          : 0;

      return {
        nome: dim.nome,
        media: Number(media.toFixed(1)),
      };
    });
  }, [respostasFiltradas]);

  const mediaGeral = useMemo(() => {
    if (!respostasFiltradas.length) return 0;

    const soma = respostasFiltradas.reduce((total, r) => total + r.mediaGeral, 0);
    return Number((soma / respostasFiltradas.length).toFixed(1));
  }, [respostasFiltradas]);

const riscoGeral = classificarRiscoMatriz(
  mediaGeral
);

  return (
    <>
      <Navbar />

      <Box sx={{ p: 3, minHeight: "100vh", backgroundColor: "#f4f6f8" }}>
        <Typography variant="h5" fontWeight={700} mb={3}>
          Controle de Respostas — Psicossocial
        </Typography>

        <Stack direction={{ xs: "column", md: "row" }} spacing={2} mb={2}>
          <FormControl size="small" sx={{ minWidth: 260 }}>
            <InputLabel>Pesquisa</InputLabel>
            <Select
              value={filtroKpi}
              label="Pesquisa"
              onChange={(e) => setFiltroKpi(e.target.value)}
            >
              <MenuItem value="">Todas</MenuItem>
              {kpis.map(([id, nome]) => (
                <MenuItem key={id} value={id}>
                  {nome}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

        
        </Stack>

        {loading && <Typography>Carregando respostas...</Typography>}

        {!loading && (
          <>
            <Box
              sx={{
                display: "grid",
                gap: 2,
                gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
                mb: 3,
              }}
            >
              <Card>
                <CardContent>
                  <Typography color="text.secondary">Total de respostas</Typography>
                  <Typography variant="h4" fontWeight={700}>
                    {respostasFiltradas.length}
                  </Typography>
                </CardContent>
              </Card>

              <Card>
                <CardContent>
                  <Typography color="text.secondary">Índice médio psicossocial</Typography>
                  <Typography variant="h4" fontWeight={700}>
                    {mediaGeral}
                  </Typography>
                </CardContent>
              </Card>

              <Card>
                <CardContent>
                  <Typography color="text.secondary">Classificação geral</Typography>
                  <Chip
                      label={riscoGeral.label}
                      sx={{
                        mt: 1,
                        backgroundColor: riscoGeral.cor,
                        color: "#000",
                        fontWeight: 700,
                      }}
                    />
                </CardContent>
              </Card>
            </Box>

          <Box
  sx={{
    display: "grid",
    gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
    gap: 2,
    mb: 3,
    alignItems: "start",
  }}
>
  <Card>
    <CardContent>
                <Typography variant="h6" fontWeight={700} mb={2}>
                  Média por dimensão
                </Typography>

                <Box sx={{ width: "100%", height: 220 }}>
                  <ResponsiveContainer>
                    <ComposedChart data={resumoDimensoes}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="nome" tick={{ fontSize: 11 }} interval={0} angle={-20} textAnchor="end" height={90} />
                      <YAxis domain={[0, 5]} ticks={[0, 1, 2, 3, 4, 5]} />
                      <Tooltip />
                     <Bar
  dataKey="media"
  name="Pontuação"
  radius={[6, 6, 0, 0]}
>
  {resumoDimensoes.map((entry, index) => (
    <Cell
      key={`cell-${index}`}
      fill={classificarRiscoMatriz(Math.round(entry.media) * 5).cor}
    />
  ))}
</Bar>
                    </ComposedChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
</Box>
            {!respostasFiltradas.length && (
              <Alert severity="info">
                Nenhuma resposta psicossocial encontrada.
              </Alert>
            )}

            {!!respostasFiltradas.length && (
              <Card>
                <CardContent>
                  <Typography variant="h6" fontWeight={700} mb={2}>
                    Respostas individuais
                  </Typography>

                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Funcionário</strong></TableCell>
                        <TableCell><strong>Setor</strong></TableCell>
                        <TableCell><strong>Data</strong></TableCell>
                        <TableCell><strong>Média</strong></TableCell>
                        <TableCell><strong>Risco</strong></TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {respostasFiltradas.map((r) => {
                       const risco = classificarRiscoMatriz(r.mediaGeral);

                        return (
                          <TableRow key={r.id} hover>
                            <TableCell>{r.usuarioNome}</TableCell>
                            <TableCell>{r.departamento}</TableCell>
                            <TableCell>{formatarData(r.createdAt)}</TableCell>
                            <TableCell>{r.mediaGeral}</TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={risco.label}
                                sx={{
                                  backgroundColor: risco.cor,
                                  color: "#000",
                                  fontWeight: 700,
                                }}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </Box>
    </>
  );
}
